/** Trust store: bundled roots + user-added PEMs persisted in localStorage. */

import * as asn1js from "asn1js";
import * as pkijs from "pkijs";

import { pemToDer, toArrayBuffer } from "../lib/bytes";
import { dnToString } from "../lib/parse";

// NOTE: sunlit-root.pem currently holds the committed *test* root; the
// production root replaces it after the offline key ceremony (see RUNBOOK).
import digicertAssured from "./pems/digicert-assured-id-root.pem?raw";
import digicertG4 from "./pems/digicert-trusted-root-g4.pem?raw";
import freetsa from "./pems/freetsa-root.pem?raw";
import sunlitRoot from "./pems/sunlit-root.pem?raw";

const STORAGE_KEY = "tsr-viewer.user-roots.v1";

export interface TrustRoot {
  id: string;
  source: "bundled" | "user";
  subject: string;
  notAfter: string;
  pem: string;
  cert: pkijs.Certificate;
}

function parsePemCerts(pem: string, source: TrustRoot["source"]): TrustRoot[] {
  return pemToDer(pem).map((der, i) => {
    const asn1 = asn1js.fromBER(toArrayBuffer(der));
    if (asn1.offset === -1) throw new Error("PEM block is not valid DER");
    const cert = new pkijs.Certificate({ schema: asn1.result });
    return {
      id: `${source}-${subjectOf(cert)}-${i}`,
      source,
      subject: subjectOf(cert),
      notAfter: cert.notAfter.value.toISOString(),
      pem,
      cert,
    };
  });
}

function subjectOf(cert: pkijs.Certificate): string {
  return dnToString(cert.subject);
}

export function bundledRoots(): TrustRoot[] {
  return [
    ...parsePemCerts(sunlitRoot, "bundled"),
    ...parsePemCerts(freetsa, "bundled"),
    ...parsePemCerts(digicertAssured, "bundled"),
    ...parsePemCerts(digicertG4, "bundled"),
  ];
}

export function loadUserRoots(): TrustRoot[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const pems: string[] = JSON.parse(raw);
    return pems.flatMap((pem) => {
      try {
        return parsePemCerts(pem, "user");
      } catch {
        return [];
      }
    });
  } catch {
    return [];
  }
}

export function addUserRoot(pem: string): TrustRoot[] {
  const parsed = parsePemCerts(pem, "user"); // throws on invalid input
  const raw = localStorage.getItem(STORAGE_KEY);
  const pems: string[] = raw ? JSON.parse(raw) : [];
  pems.push(pem);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(pems));
  return parsed;
}

export function removeUserRoot(subject: string): void {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return;
  const pems: string[] = JSON.parse(raw);
  const kept = pems.filter((pem) => {
    try {
      return !parsePemCerts(pem, "user").some((r) => r.subject === subject);
    } catch {
      return false;
    }
  });
  localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
}
